/**
 * DowngradeBanner - Shows scheduled plan change notifications (downgrades and cancellations)
 * Implements Step 18a from Change-Billing.md + Step 21 cancellation support
 *
 * Variants:
 * - 'wallet': Yellow/amber banner for WalletPage (below plan info row)
 * - 'modal': Blue banner for ChangePlanModal (top of modal content)
 *
 * Types:
 * - 'downgrade': Scheduled plan downgrade (shows target plan name)
 * - 'cancellation': Scheduled plan cancellation (no target plan)
 */

import { AlertTriangle, Info, Loader2, XCircle } from 'lucide-react';

interface DowngradeBannerProps {
  variant: 'wallet' | 'modal';
  type?: 'downgrade' | 'cancellation';
  scheduledPlanName?: string;
  scheduledDate: string;
  onCancel: () => void;
  cancelling?: boolean;
}

export function DowngradeBanner({
  variant,
  type = 'downgrade',
  scheduledPlanName,
  scheduledDate,
  onCancel,
  cancelling = false,
}: DowngradeBannerProps) {
  // Format date for display
  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formattedDate = formatDate(scheduledDate);
  const isCancellation = type === 'cancellation';

  if (variant === 'wallet') {
    // Wallet Page Banner - Red for cancellation, Amber for downgrade
    if (isCancellation) {
      return (
        <div className="mt-6 bg-red-50 border border-red-400 rounded-lg p-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <XCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <span className="text-sm font-medium text-red-800">
              Your plan will be <strong className="text-red-900">cancelled</strong> on{' '}
              <strong className="text-red-900">{formattedDate}</strong>
            </span>
          </div>
          <button
            onClick={onCancel}
            disabled={cancelling}
            className="px-4 py-2 text-sm font-semibold text-red-800 bg-white border border-red-600 rounded hover:bg-red-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap flex items-center gap-2"
          >
            {cancelling ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Keeping Plan...
              </>
            ) : (
              'Keep My Plan'
            )}
          </button>
        </div>
      );
    }

    // Wallet Page Banner - Yellow/amber warning style for downgrade
    return (
      <div className="mt-6 bg-amber-50 border border-amber-400 rounded-lg p-4 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
          <span className="text-sm font-medium text-amber-800">
            Changing to <strong className="text-amber-900">{scheduledPlanName}</strong> on{' '}
            <strong className="text-amber-900">{formattedDate}</strong>
          </span>
        </div>
        <button
          onClick={onCancel}
          disabled={cancelling}
          className="px-4 py-2 text-sm font-semibold text-amber-800 bg-white border border-amber-600 rounded hover:bg-amber-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap flex items-center gap-2"
        >
          {cancelling ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Cancelling...
            </>
          ) : (
            'Cancel'
          )}
        </button>
      </div>
    );
  }

  // Modal Banner variants
  if (isCancellation) {
    // Red/warning style for cancellation
    return (
      <div className="bg-red-50 border border-red-400 rounded-lg p-4 mb-6">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <div className="text-sm font-semibold text-red-900">
                Your plan is scheduled for cancellation
              </div>
              <div className="text-sm text-red-700 mt-1">
                Your plan will be cancelled on <strong>{formattedDate}</strong>
              </div>
            </div>
          </div>
          <button
            onClick={onCancel}
            disabled={cancelling}
            className="px-4 py-2 text-sm font-semibold text-red-700 bg-white border border-red-500 rounded hover:bg-red-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap flex items-center gap-2"
          >
            {cancelling ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Keeping Plan...
              </>
            ) : (
              'Keep My Plan'
            )}
          </button>
        </div>
      </div>
    );
  }

  // Modal Banner - Blue info style for downgrade
  return (
    <div className="bg-blue-50 border border-blue-400 rounded-lg p-4 mb-6">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <div className="text-sm font-semibold text-blue-900">
              You have a scheduled plan change
            </div>
            <div className="text-sm text-blue-700 mt-1">
              Downgrading to <strong>{scheduledPlanName}</strong> on{' '}
              <strong>{formattedDate}</strong>
            </div>
          </div>
        </div>
        <button
          onClick={onCancel}
          disabled={cancelling}
          className="px-4 py-2 text-sm font-semibold text-blue-700 bg-white border border-blue-500 rounded hover:bg-blue-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap flex items-center gap-2"
        >
          {cancelling ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Cancelling...
            </>
          ) : (
            'Cancel Downgrade'
          )}
        </button>
      </div>
    </div>
  );
}
